import { create } from 'zustand';
import type { ChannelName } from '@/types';

/**
 * Stream store.
 *
 * Holds bounded per-channel ring buffers of the most recent events. The
 * canonical event types live in @tiw/shared-types; we keep this
 * store generic to avoid coupling the HUD to a single payload shape.
 *
 * See docs/EVENT_ARCHITECTURE.md.
 */
export const STREAM_BUFFER_SIZE = 256;

export interface StreamEvent<TPayload = unknown> {
  readonly id: string;
  readonly channel: ChannelName;
  readonly ts: number;
  readonly payload: TPayload;
}

interface StreamState {
  readonly byChannel: Readonly<Record<string, readonly StreamEvent[]>>;
  push: (event: StreamEvent) => void;
}

function appendBounded(
  prev: readonly StreamEvent[] | undefined,
  next: StreamEvent,
): readonly StreamEvent[] {
  const list = prev ? [...prev, next] : [next];
  if (list.length <= STREAM_BUFFER_SIZE) return list;
  return list.slice(list.length - STREAM_BUFFER_SIZE);
}

export const useStreamStore = create<StreamState>((set) => ({
  byChannel: {},
  push: (event) =>
    set((state) => ({
      byChannel: {
        ...state.byChannel,
        [event.channel]: appendBounded(state.byChannel[event.channel], event),
      },
    })),
}));
